import { ErroApi } from '@/nucleo/auth/cliente-api';
import { useMeusTermos } from '@/modulos/portal-credenciado/contratos/dados';
import { useOrdensDoCredenciado } from '@/modulos/portal-credenciado/ordens-servico/dados';

import type { AlertaDeVencimento } from './contrato';
import { useAlertasDeVencimento } from './vencimentos';

/**
 * Camada de dados do painel inicial. Tudo vem da API — não há mock neste
 * diretório (ver `README.md`).
 *
 * Cada indicador já chega decidido para a tela: o 403 não é falha, é "este
 * papel não alcança esta informação", e o indicador simplesmente não é
 * renderizado (regra 7 de interface). Assim o `ADMIN_CREDENCIADO`, o
 * `OPERADOR_CREDENCIADO` e o `FINANCEIRO_CREDENCIADO`, que "Meus termos" recusa
 * (a rota aceita só `CREDENCIADO`), veem o painel sem tela de erro.
 */
export interface IndicadorDoPainel<V = number> {
  /** Primeira busca em andamento. */
  carregando: boolean;
  /** Falha que a tela mostra como falha. Nunca o 403. */
  erro: unknown;
  /** A API recusou com 403: o indicador não é renderizado. */
  recusado: boolean;
  /** `undefined` enquanto carrega, na falha e na recusa. */
  valor: V | undefined;
  tentarDeNovo: () => void;
}

function ehRecusa(erro: unknown): boolean {
  return erro instanceof ErroApi && erro.pedeOutroCaminho;
}

/** Os Termos de Adesão `ATIVO` do credenciado, de "Meus termos". */
export function useContratosAtivos(): IndicadorDoPainel {
  const termos = useMeusTermos();
  const recusado = ehRecusa(termos.error);
  return {
    carregando: termos.isPending,
    erro: recusado ? null : (termos.error ?? null),
    recusado,
    valor: termos.data?.filter((termo) => termo.status === 'ATIVO').length,
    tentarDeNovo: () => void termos.refetch(),
  };
}

/**
 * As Ordens de Serviço ativas, da projeção J-06/S03 — a mesma consulta e o
 * mesmo cache de Contratos e Termos (`CHAVES_DE_OS`).
 *
 * Sem Termo ativo a projeção nem é consultada e o número é 0: não há OS sem
 * termo, e isso não é falha. Já a recusa — de "Meus termos", ou da projeção
 * com um termo ativo na mão — tira o indicador da tela. O gancho de OS junta
 * os três casos em `semTermoAtivo`; aqui eles se separam pela própria lista
 * de termos.
 */
export function useOsAtivas(): IndicadorDoPainel {
  const termos = useMeusTermos();
  const ordens = useOrdensDoCredenciado((lista) => lista.length);
  const haTermoAtivo = termos.data?.some((termo) => termo.status === 'ATIVO') ?? false;
  const recusado = ehRecusa(termos.error) || (haTermoAtivo && ordens.semTermoAtivo);

  if (recusado) {
    return { carregando: false, erro: null, recusado, valor: undefined, tentarDeNovo: noop };
  }
  return {
    carregando: ordens.carregando,
    erro: ordens.erro,
    recusado: false,
    valor: ordens.semTermoAtivo ? 0 : ordens.dados,
    tentarDeNovo: ordens.tentarDeNovo,
  };
}

/** Os documentos que vencem em até 60 dias, ou já venceram (`vencimentos.ts`). */
export function useVencimentosProximos(): IndicadorDoPainel<AlertaDeVencimento[]> {
  const alertas = useAlertasDeVencimento();
  const recusado = ehRecusa(alertas.error);
  return {
    carregando: alertas.isPending,
    erro: recusado ? null : (alertas.error ?? null),
    recusado,
    valor: alertas.data,
    tentarDeNovo: () => void alertas.refetch(),
  };
}

function noop(): void {}
